import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader, CButton, SEO } from '../../components/common';
import { FiShield, FiBriefcase, FiGlobe, FiVideo, FiMessageCircle, FiChevronDown, FiChevronUp, FiTarget } from 'react-icons/fi';
import { IoStar, IoStarOutline } from 'react-icons/io5';
import { fetchExpertDetails as fetchExpertDetailsAPI, fetchExpertRatings as fetchExpertRatingsAPI, fetchTrackRecord, Expert, Rating, TrackRecord } from '../../api';
import { returnImageURIFromBytes } from '../../utils/constants';

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English', hi: 'Hindi', mr: 'Marathi', ta: 'Tamil', te: 'Telugu',
  kn: 'Kannada', bn: 'Bengali', gu: 'Gujarati', ml: 'Malayalam', pa: 'Punjabi',
};

const getAvailabilityBadge = (hours: number | null) => {
  if (hours === null || hours === undefined) return { text: 'Check availability', color: '#94a3b8' };
  if (hours <= 2) return { text: 'Available in 2h', color: '#22c55e' };
  if (hours <= 24) return { text: 'Available today', color: '#22c55e' };
  if (hours <= 72) return { text: 'Available this week', color: '#f59e0b' };
  return { text: 'Check availability', color: '#94a3b8' };
};

const toTitleCase = (slug: string) =>
  slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const ExpertProfile: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(true);
  const [expert, setExpert] = useState<Expert | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [trackRecord, setTrackRecord] = useState<TrackRecord | null>(null);

  useEffect(() => {
    fetchExpertDetails();
    fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTrackRecord = async (expertProfileId: number) => {
    try {
      const data = await fetchTrackRecord(expertProfileId);
      if (data.total_calls > 0) setTrackRecord(data);
    } catch {
      // track record not available
    }
  };

  const fetchExpertDetails = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const data = await fetchExpertDetailsAPI(parseInt(id));
      setExpert(data);
      if (data.has_track_record || (data.total_calls_made && data.total_calls_made > 0)) {
        // expert_id from profile, need to get it from track_record endpoint using user_id
        // The track_record endpoint expects expert_id (user_profile.id), not user_auth.id
        // But fetchExpertDetails returns user_id (auth id). We pass it through.
        loadTrackRecord(parseInt(id));
      }
    } catch (err: any) {
      const status = err?.response?.status;
      setErrorStatus(status ?? null);
      if (status === 404 || status === 400) {
        setError(err?.response?.data?.error || 'Expert not found');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    if (!id) return;
    try {
      const data = await fetchExpertRatingsAPI(parseInt(id));
      setRatings(data);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const handleBookChat = () => navigate(`/expert/${id}/book?type=chat`);
  const handleBookCall = () => navigate(`/expert/${id}/book?type=call`);

  const formatDate = (isoString: string) => new Date(isoString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  const renderStars = (rating: number, size = 12) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i}>
          {i < Math.floor(rating)
            ? React.createElement(IoStar as any, { size, color: '#FFD700' })
            : React.createElement(IoStarOutline as any, { size, color: colors.grayScale400 })
          }
        </span>
      );
    }
    return <div className="flex gap-0.5">{stars}</div>;
  };

  if (loading) {
    return (
      <div className="p-4 min-h-screen pb-8" style={{ backgroundColor: colors.backgroundColor }}>
        <CLoader />
      </div>
    );
  }

  if (error || !expert) {
    const isNotFound = errorStatus === 404 || errorStatus === 400;
    return (
      <div className="p-4 min-h-screen pb-8 flex flex-col items-center justify-center gap-4" style={{ backgroundColor: colors.backgroundColor }}>
        <CText type="B18" color={colors.textColor}>{error || 'Expert not found'}</CText>
        {isNotFound ? (
          <CButton title="Go Back" onPress={() => navigate(-1)} />
        ) : (
          <CButton title="Retry" onPress={fetchExpertDetails} />
        )}
      </div>
    );
  }

  const firstName = expert.name.split(' ')[0];
  const minRate = Math.min(expert.call_session_rate, expert.chat_session_rate);
  const availability = getAvailabilityBadge(expert.hours_until_available);
  const distribution = [5, 4, 3, 2, 1].map(star => ratings.filter(r => Math.floor(r.rating) === star).length);
  const totalRatings = ratings.length;
  const languages = expert.languages.map(l => LANGUAGE_NAMES[l] || l).join(', ');
  const displayedReviews = reviewsExpanded ? ratings : ratings.slice(0, 3);

  const seoDescription = `Book a ${expert.call_session_rate <= expert.chat_session_rate ? 'call' : 'chat'} session with ${expert.name}, a SEBI-registered ${expert.sebi_qualification} with ${expert.years_of_xp} years experience. ${expert.total_completed_sessions}+ consultations completed. Starting at Rs.${minRate}/session on Yantrai.`;

  const jsonLdSchemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: `${expert.name} — ${expert.sebi_qualification}`,
      description: expert.bio || expert.headline || `SEBI-registered financial expert with ${expert.years_of_xp} years of experience.`,
      provider: { '@type': 'Organization', name: 'Yantrai', url: 'https://yantrai.com' },
      areaServed: 'India',
      makesOffer: [
        { '@type': 'Offer', name: 'Video Call Session', price: String(expert.call_session_rate), priceCurrency: 'INR' },
        { '@type': 'Offer', name: 'Chat Session', price: String(expert.chat_session_rate), priceCurrency: 'INR' },
      ],
      ...(expert.user_avg_rating > 0 && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: expert.user_avg_rating.toFixed(1),
          bestRating: '5',
          ratingCount: String(totalRatings || 1),
        },
      }),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://yantrai.com/' },
        { '@type': 'ListItem', position: 2, name: 'Experts', item: 'https://yantrai.com/experts' },
        { '@type': 'ListItem', position: 3, name: expert.name },
      ],
    },
  ];

  // --- Shared sub-sections ---

  const heroSection = (
    <div
      className="rounded-none lg:rounded-2xl p-6 lg:p-8"
      style={{ background: `linear-gradient(135deg, ${colors.gradient1}, ${colors.gradient2})` }}
    >
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
        {/* Avatar */}
        {expert.image ? (
          <img
            src={returnImageURIFromBytes(expert.image)}
            alt={expert.name}
            className="w-20 h-20 rounded-full object-cover border-4"
            style={{ borderColor: 'rgba(255,255,255,0.3)' }}
          />
        ) : (
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold border-4 shrink-0"
            style={{ background: 'rgba(255,255,255,0.2)', color: colors.white, borderColor: 'rgba(255,255,255,0.3)' }}
          >
            {expert.name.charAt(0)}
          </div>
        )}

        <div className="text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <CText type="B24" color={colors.white}>{expert.name}</CText>
            {expert.is_verified && React.createElement(FiShield as any, { size: 20, color: colors.white })}
          </div>
          {expert.headline && (
            <CText type="r14" color="rgba(255,255,255,0.8)" style={{ marginTop: 4 }}>
              {expert.headline}
            </CText>
          )}
          <div className="flex items-center justify-center sm:justify-start gap-2 mt-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: availability.color }} />
            <CText type="r12" color="rgba(255,255,255,0.9)">{availability.text}</CText>
          </div>
          {expert.company_associated && expert.company_name && (
            <div className="flex items-center justify-center sm:justify-start gap-1.5 mt-1">
              {React.createElement(FiBriefcase as any, { size: 12, color: 'rgba(255,255,255,0.7)' })}
              <CText type="r12" color="rgba(255,255,255,0.7)">{expert.company_name}</CText>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const credentialBadges = (
    <div className="flex overflow-x-auto lg:flex-wrap gap-2 pb-1">
      <span className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: colors.primary50, color: colors.primary }}>
        {React.createElement(FiShield as any, { size: 12 })}
        {expert.sebi_qualification}
      </span>
      <span className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs font-mono" style={{ backgroundColor: colors.primary50, color: colors.primary }}>
        SEBI: {expert.sebi_reg_number}
      </span>
      <span className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: colors.primary50, color: colors.primary }}>
        {React.createElement(FiBriefcase as any, { size: 12 })}
        {expert.years_of_xp} yrs experience
      </span>
      {languages && (
        <span className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: colors.primary50, color: colors.primary }}>
          {React.createElement(FiGlobe as any, { size: 12 })}
          {languages}
        </span>
      )}
    </div>
  );

  const statsRow = (
    <div className="grid grid-cols-3 gap-3">
      {[
        { value: expert.user_avg_rating > 0 ? expert.user_avg_rating.toFixed(1) : 'New', label: 'Avg Rating', icon: <span>{React.createElement(IoStar as any, { size: 16, color: '#FFD700' })}</span> },
        { value: String(expert.total_completed_sessions), label: 'Sessions' },
        { value: String(expert.years_of_xp), label: 'Years Exp' },
      ].map((stat, i) => (
        <div key={i} className="rounded-xl p-4 text-center" style={{ backgroundColor: colors.inputBackground }}>
          <div className="flex items-center justify-center gap-1">
            {stat.icon}
            <CText type="B24" color={colors.primary}>{stat.value}</CText>
          </div>
          <CText type="r12" color={colors.grayScale500}>{stat.label}</CText>
        </div>
      ))}
    </div>
  );

  const trackRecordSection = trackRecord ? (
    <div>
      <CText type="B18" color={colors.textColor} style={{ marginBottom: 12 }}>Verified Track Record</CText>

      {/* Badges */}
      {trackRecord.badges.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {trackRecord.badges.map(badge => (
            <span
              key={badge.slug}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: '#22c55e20', color: '#22c55e' }}
            >
              {React.createElement(FiTarget as any, { size: 12 })}
              {badge.label}
            </span>
          ))}
        </div>
      )}

      {/* Mini stats */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: colors.inputBackground }}>
          <CText type="B18" color={colors.primary}>{trackRecord.total_calls}</CText>
          <CText type="r10" color={colors.grayScale500}>Calls</CText>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: colors.inputBackground }}>
          <CText type="B18" color="#22c55e">{trackRecord.accuracy_pct.toFixed(1)}%</CText>
          <CText type="r10" color={colors.grayScale500}>Accuracy</CText>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: colors.inputBackground }}>
          <CText type="B18" color={trackRecord.avg_return_pct >= 0 ? '#22c55e' : '#ef4444'}>
            {trackRecord.avg_return_pct >= 0 ? '+' : ''}{trackRecord.avg_return_pct.toFixed(1)}%
          </CText>
          <CText type="r10" color={colors.grayScale500}>Avg Return</CText>
        </div>
      </div>

      {/* Recent resolved calls */}
      {trackRecord.recent_calls.length > 0 && (
        <div className="space-y-2">
          {trackRecord.recent_calls.slice(0, 3).map(call => (
            <div
              key={call.id}
              className="flex items-center justify-between rounded-lg px-3 py-2"
              style={{ backgroundColor: colors.inputBackground }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                  style={{
                    backgroundColor: call.call_type === 'BUY' ? '#22c55e20' : '#ef444420',
                    color: call.call_type === 'BUY' ? '#22c55e' : '#ef4444',
                  }}
                >
                  {call.call_type}
                </span>
                <CText type="M12" color={colors.textColor}>{call.ticker_symbol}</CText>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  style={{
                    backgroundColor: call.status === 'TARGET_HIT' ? '#22c55e20' : call.status === 'SL_HIT' ? '#ef444420' : '#6b728020',
                    color: call.status === 'TARGET_HIT' ? '#22c55e' : call.status === 'SL_HIT' ? '#ef4444' : '#6b7280',
                  }}
                >
                  {call.status.replace('_', ' ')}
                </span>
                {call.return_pct !== null && (
                  <CText type="M12" color={call.return_pct >= 0 ? '#22c55e' : '#ef4444'}>
                    {call.return_pct >= 0 ? '+' : ''}{call.return_pct}%
                  </CText>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {trackRecord.total_calls > 3 && (
        <button
          onClick={() => navigate(`/expert/${id}/calls`)}
          className="mt-2 border-none bg-transparent cursor-pointer p-0"
          style={{ color: colors.primary }}
        >
          <CText type="M12" color={colors.primary}>View all {trackRecord.total_calls} calls</CText>
        </button>
      )}
    </div>
  ) : null;

  const aboutSection = (
    <div>
      <CText type="B18" color={colors.textColor} style={{ marginBottom: 8 }}>About {firstName}</CText>
      {expert.bio ? (
        <div>
          <CText
            type="r14"
            color={colors.textColor}
            className={!bioExpanded ? 'line-clamp-4' : ''}
            style={{ lineHeight: '1.6' }}
          >
            {expert.bio}
          </CText>
          {expert.bio.length > 200 && (
            <button
              onClick={() => setBioExpanded(!bioExpanded)}
              className="flex items-center gap-1 mt-1 border-none bg-transparent cursor-pointer p-0"
              style={{ color: colors.primary }}
            >
              <CText type="M12" color={colors.primary}>{bioExpanded ? 'Show less' : 'Read more'}</CText>
              {bioExpanded
                ? React.createElement(FiChevronUp as any, { size: 14 })
                : React.createElement(FiChevronDown as any, { size: 14 })
              }
            </button>
          )}
        </div>
      ) : (
        <CText type="r14" color={colors.grayScale500}>No bio available.</CText>
      )}
      {expert.specializations?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {expert.specializations.map((spec) => (
            <span
              key={spec}
              className="px-3 py-1 rounded-full text-xs"
              style={{ backgroundColor: colors.primary + '15', color: colors.primary }}
            >
              {toTitleCase(spec)}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const pricingSection = (
    <div className="grid grid-cols-2 gap-3">
      {/* Call Card */}
      <div className="rounded-xl p-4 text-center border" style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}>
        {React.createElement(FiVideo as any, { size: 24, color: colors.primary, className: 'mx-auto' })}
        <CText type="M14" color={colors.textColor} style={{ marginTop: 8 }}>Video Call</CText>
        <CText type="B24" color={colors.primary} style={{ marginTop: 4 }}>₹{expert.call_session_rate}</CText>
        <CText type="r12" color={colors.grayScale500}>/30 min session</CText>
        <CButton title="Book Call" onPress={handleBookCall} size="sm" className="!w-full !lg:w-full !mt-3 !lg:min-w-0 !lg:max-w-none !lg:px-2" />
      </div>
      {/* Chat Card */}
      <div className="rounded-xl p-4 text-center border" style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}>
        {React.createElement(FiMessageCircle as any, { size: 24, color: colors.primary, className: 'mx-auto' })}
        <CText type="M14" color={colors.textColor} style={{ marginTop: 8 }}>Chat Session</CText>
        <CText type="B24" color={colors.primary} style={{ marginTop: 4 }}>₹{expert.chat_session_rate}</CText>
        <CText type="r12" color={colors.grayScale500}>/30 min session</CText>
        <CButton title="Book Chat" onPress={handleBookChat} variant="outline" size="sm" className="!w-full !lg:w-full !mt-3 !lg:min-w-0 !lg:max-w-none !lg:px-2" />
      </div>
    </div>
  );

  const reviewsSection = (
    <div>
      <CText type="B18" color={colors.textColor} style={{ marginBottom: 12 }}>Reviews</CText>

      {totalRatings > 0 ? (
        <>
          {/* Rating Summary */}
          <div className="flex gap-4 items-start mb-4 rounded-xl p-4" style={{ backgroundColor: colors.inputBackground }}>
            {/* Left: big number */}
            <div className="text-center shrink-0">
              <CText type="B36" color={colors.primary}>{expert.user_avg_rating.toFixed(1)}</CText>
              {renderStars(expert.user_avg_rating, 14)}
              <CText type="r12" color={colors.grayScale500} style={{ marginTop: 4 }}>{totalRatings} review{totalRatings !== 1 ? 's' : ''}</CText>
            </div>
            {/* Right: distribution bars */}
            <div className="flex-1 space-y-1.5">
              {distribution.map((count, i) => {
                const star = 5 - i;
                const pct = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
                return (
                  <div key={star} className="flex items-center gap-2">
                    <CText type="r12" color={colors.grayScale500} className="w-3 text-right">{star}</CText>
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.borderColor }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors.primary }} />
                    </div>
                    <CText type="r12" color={colors.grayScale500} className="w-5 text-right">{count}</CText>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Individual Reviews */}
          <div className="space-y-3">
            {displayedReviews.map((rating) => (
              <div key={rating.id} className="rounded-xl p-4" style={{ backgroundColor: colors.inputBackground }}>
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
                    style={{ backgroundColor: colors.primary50, color: colors.primary }}
                  >
                    {rating.user_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <CText type="M14" color={colors.textColor}>{rating.user_name}</CText>
                      <CText type="r12" color={colors.grayScale500}>{formatDate(rating.created_at)}</CText>
                    </div>
                    {renderStars(rating.rating)}
                  </div>
                </div>
                {rating.feedback && (
                  <CText type="r14" color={colors.grayScale400} style={{ marginTop: 4 }}>{rating.feedback}</CText>
                )}
              </div>
            ))}
          </div>

          {totalRatings > 3 && (
            <button
              onClick={() => setReviewsExpanded(!reviewsExpanded)}
              className="flex items-center gap-1 mt-3 border-none bg-transparent cursor-pointer p-0"
              style={{ color: colors.primary }}
            >
              <CText type="M14" color={colors.primary}>
                {reviewsExpanded ? 'Show fewer reviews' : `Show all ${totalRatings} reviews`}
              </CText>
              {reviewsExpanded
                ? React.createElement(FiChevronUp as any, { size: 16 })
                : React.createElement(FiChevronDown as any, { size: 16 })
              }
            </button>
          )}
        </>
      ) : (
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: colors.inputBackground }}>
          <CText type="r14" color={colors.grayScale500}>No reviews yet. Be the first to book a session!</CText>
          <CButton title="Book Now" onPress={handleBookCall} size="sm" className="!mx-auto" />
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen pb-20 lg:pb-8" style={{ backgroundColor: colors.backgroundColor }}>
      <SEO
        title={`${expert.name} — ${expert.sebi_qualification} | Book SEBI Expert Advisor`}
        description={seoDescription}
        canonical={`/expert/${id}`}
        ogType="profile"
        jsonLd={jsonLdSchemas}
      />

      {/* Breadcrumb */}
      <div className="px-4 py-3 lg:px-8">
        <div className="flex items-center gap-1.5">
          <Link to="/" className="no-underline"><CText type="r12" color={colors.grayScale500}>Home</CText></Link>
          <CText type="r12" color={colors.grayScale500}>/</CText>
          <Link to="/experts" className="no-underline"><CText type="r12" color={colors.grayScale500}>Experts</CText></Link>
          <CText type="r12" color={colors.grayScale500}>/</CText>
          <CText type="r12" color={colors.textColor}>{expert.name}</CText>
        </div>
      </div>

      {/* Desktop: two-column layout */}
      <div className="lg:grid lg:grid-cols-3 lg:gap-6 lg:px-8 lg:max-w-6xl lg:mx-auto">
        {/* Left column (main content) */}
        <div className="lg:col-span-2 space-y-6">
          {heroSection}

          {/* Mobile-only: credentials + stats below hero */}
          <div className="px-4 lg:hidden space-y-4">
            {credentialBadges}
            {statsRow}
          </div>

          <div className="px-4 lg:px-0 space-y-6">
            {trackRecordSection}
            {aboutSection}

            {/* Mobile-only pricing */}
            <div className="lg:hidden">
              {pricingSection}
            </div>

            {reviewsSection}
          </div>
        </div>

        {/* Right column (sidebar, desktop only) */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="sticky top-4 space-y-4">
            {pricingSection}
            <div className="rounded-xl p-4" style={{ backgroundColor: colors.inputBackground }}>
              {credentialBadges}
            </div>
            {statsRow}
            <div className="flex items-center gap-2 px-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: availability.color }} />
              <CText type="r12" color={colors.grayScale500}>{availability.text}</CText>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 lg:hidden px-4 py-3 flex items-center justify-between border-t"
        style={{ backgroundColor: colors.backgroundColor, borderColor: colors.borderColor }}
      >
        <div>
          <CText type="B18" color={colors.primary}>₹{minRate}</CText>
          <CText type="r12" color={colors.grayScale500}>/session onwards</CText>
        </div>
        <CButton title="Book Now" onPress={handleBookCall} size="sm" className="!w-auto !mt-0 !px-6" />
      </div>
    </div>
  );
};

export default ExpertProfile;
